import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@repo/ui/lib/utils';

const badgeVariants = cva(
  'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90 border-transparent',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 border-transparent',
        destructive:
          'bg-destructive [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 border-transparent text-white',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        // v2 design system status pills ("Within limit", "Needs review",
        // "Below minimum", "Pre-deposit", ...) — soft-tinted glass-adjacent
        // fills built from the v2 semantic tokens, using Tailwind's color
        // opacity modifier for the warning/error washes so we don't need
        // separate "soft" tokens for each status color.
        success:
          'border-transparent bg-rowa-brand-mint-soft text-rowa-status-positive [a&]:hover:bg-rowa-brand-mint-soft/80',
        warning:
          'border-transparent bg-rowa-status-warning/12 text-rowa-status-warning [a&]:hover:bg-rowa-status-warning/20',
        error:
          'border-transparent bg-rowa-status-error/12 text-rowa-status-error [a&]:hover:bg-rowa-status-error/20',
        neutral:
          'border-rowa-border-default bg-rowa-surface-panel text-rowa-text-secondary [a&]:hover:bg-rowa-surface-panel/70'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
